import {
  RemoteVideoAria,
  RemoteVideoContainer,
} from '@components/RemoteVideo/style';
import useApplyVideoObjectFit from '@hooks/ref/useApplyVideoObjectFit';
import type { BaseComponentProps } from '@interfaces/selection';
import { useAudioVideo } from '@providers/AudioVideoProvider';
import type { FC, HTMLAttributes, PropsWithChildren } from 'react';
import { useEffect, useRef } from 'react';

interface RemoteVideoProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'css'>, BaseComponentProps {
  size?: number;
  /** The tile ID to bind the video element to */
  tileId: number;
  /** The name to show on the video's nameplate */
  name?: string;

  attendeeId: string;
}

const RemoteVideo: FC<PropsWithChildren<RemoteVideoProps>> = ({
  name,
  className,
  size,
  tileId,
  attendeeId,
  ...rest
}) => {
  const audioVideo = useAudioVideo();
  const videoEl = useRef<HTMLVideoElement | null>(null);
  useApplyVideoObjectFit(videoEl);

  useEffect(() => {
    if (!audioVideo || !videoEl.current) {
      return;
    }

    audioVideo.bindVideoElement(tileId, videoEl.current!);

    return () => {
      const tile = audioVideo.getVideoTile(tileId);
      if (tile) {
        audioVideo.unbindVideoElement(tileId);
      }
    };
  }, [audioVideo, tileId]);

  if (!attendeeId) return null;

  return (
    <RemoteVideoAria>
      <RemoteVideoContainer
        {...rest}
        ref={videoEl}
        nameplate={name}
        className={`ch-remote-video--${tileId} ${className || ''}`}
        attendeeId={attendeeId}
        videoTileType={'interviewer'}
        size={size}
      />
    </RemoteVideoAria>
  );
};

export default RemoteVideo;
